// Vovy text-to-speech client. Audio is synthesized by the `tts` Supabase edge
// function, which holds the ElevenLabs API key server-side (set ELEVENLABS_API_KEY
// in the project's Edge Function secrets) — the key is never shipped to the browser.

import { supabase } from '@/integrations/supabase/client';

export type VoiceStyle = 'energetic' | 'chill';

let currentAudio: HTMLAudioElement | null = null;
let muted = false;
let style: VoiceStyle = 'energetic';
const cache = new Map<string, string>(); // `${style}:${text}` -> object URL

export function isVoiceConfigured() {
  // Synthesis runs server-side; assume available (speak() degrades gracefully
  // if the edge function or its ELEVENLABS_API_KEY secret isn't set).
  return true;
}

export function setVoiceStyle(value: VoiceStyle) {
  style = value;
}

/** Mute/unmute Vovy. Muting also stops anything currently playing. */
export function setMuted(value: boolean) {
  muted = value;
  if (muted) stopVoice();
}

export function isMuted() {
  return muted;
}

// Bumped on every stop/speak so an in-flight request that resolves late
// (e.g. React StrictMode double-invoking effects) never plays over a newer one.
let playToken = 0;

export function stopVoice() {
  playToken++;
  if (currentAudio) {
    try { currentAudio.pause(); } catch {}
    currentAudio = null;
  }
}

// Synthesize `text` to a playable object-URL and cache it. Returns null on
// failure. Shared by speak() and prefetch() so a prefetched line plays instantly.
const inflight = new Map<string, Promise<string | null>>();
async function synthToUrl(text: string, useStyle: VoiceStyle): Promise<string | null> {
  const cacheKey = `${useStyle}:${text}`;
  const cached = cache.get(cacheKey);
  if (cached) return cached;
  const pending = inflight.get(cacheKey);
  if (pending) return pending; // de-dupe concurrent prefetch + speak of the same line

  const p = (async () => {
    try {
      const { data, error } = await supabase.functions.invoke('tts', {
        body: { text, style: useStyle },
      });
      if (error) throw error;
      // tts returns application/octet-stream, so supabase-js gives a Blob/ArrayBuffer.
      // Re-wrap as audio/mpeg so the <audio> element plays it.
      const blob = new Blob([data as BlobPart], { type: 'audio/mpeg' });
      if (blob.size === 0) throw new Error('Empty audio from tts');
      const url = URL.createObjectURL(blob);
      cache.set(cacheKey, url);
      return url;
    } catch (err) {
      console.warn('[vovyVoice] TTS synth failed:', err);
      return null;
    } finally {
      inflight.delete(cacheKey);
    }
  })();
  inflight.set(cacheKey, p);
  return p;
}

/** Warm the cache for a line (no playback). Use to prefetch a whole unit's lines
 *  on load so each step plays instantly and the API calls batch upfront. */
export async function prefetch(text: string, styleOverride?: VoiceStyle): Promise<void> {
  if (!text) return;
  await synthToUrl(text, styleOverride ?? style);
}

/** Prefetch many lines in parallel (batches the unit's TTS up front). */
export function prefetchMany(texts: string[], styleOverride?: VoiceStyle): void {
  const useStyle = styleOverride ?? style;
  for (const t of texts) {
    if (t) void synthToUrl(t, useStyle);
  }
}

/** Speak `text` in Vovy's voice. No-ops gracefully when muted. Plays instantly
 *  if the line was prefetched. Pass `styleOverride` to force a specific voice. */
export async function speak(text: string, styleOverride?: VoiceStyle): Promise<void> {
  if (!text || muted) return;

  stopVoice();
  const myToken = ++playToken;
  const useStyle = styleOverride ?? style;

  const url = await synthToUrl(text, useStyle);
  if (!url) return;

  // A newer speak()/stopVoice() happened while we were fetching — bail so we
  // don't play over it (prevents the "two voices at once" overlap).
  if (muted || myToken !== playToken) return;

  const audio = new Audio(url);
  currentAudio = audio;
  await new Promise<void>((resolve) => {
    const done = () => resolve();
    audio.addEventListener('ended', done, { once: true });
    audio.addEventListener('pause', done, { once: true });
    audio.addEventListener('error', done, { once: true });
    audio.play().catch(() => resolve());
  });
}
